import { useState, useEffect } from 'react'
import {
  ArrowLeft,
  Globe,
  Trash2,
  Pencil,
  X,
  Loader2,
  Check,
  AlertCircle
} from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  getAllNetworks,
  removeCustomNetwork,
  updateCustomNetwork
} from '@/config/networks'
import networkValidationService from '@/services/blockchain/network-validation.service'
import providerService from '@/services/blockchain/provider.service'
import { useWalletStore } from '@/store/walletStore'
import Snackbar from '@/components/Snackbar'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog'

type RpcStatus = 'idle' | 'validating' | 'valid' | 'invalid' | 'exists'

const NetworkDetails = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const selectedNetwork = useWalletStore((state) => state.selectedNetwork)
  const setSelectedNetwork = useWalletStore((state) => state.setSelectedNetwork)
  const resetForWalletSwitch = useWalletStore(
    (state) => state.resetForWalletSwitch
  )

  const [isEditing, setIsEditing] = useState(false)
  const [networkName, setNetworkName] = useState('')
  const [rpcName, setRpcName] = useState('')
  const [rpcUrl, setRpcUrl] = useState('')
  const [detectedChainId, setDetectedChainId] = useState<number | null>(null)
  const [currencySymbol, setCurrencySymbol] = useState('')
  const [blockExplorerUrl, setBlockExplorerUrl] = useState('')
  const [rpcStatus, setRpcStatus] = useState<RpcStatus>('idle')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [snackbarState, setSnackbarState] = useState<{
    isOpen: boolean
    message: string
    type?: 'success' | 'error' | 'info'
  }>({ isOpen: false, message: '' })
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false)
  const [showChainIdConfirm, setShowChainIdConfirm] = useState(false)

  const network = getAllNetworks().find((n) => n.id === id)

  useEffect(() => {
    if (!isEditing || !network) return

    if (rpcUrl.trim() === network.rpcUrl) {
      setRpcStatus('valid')
      setDetectedChainId(network.chainId)
      return
    }

    setRpcStatus('idle')
    setDetectedChainId(null)

    if (!rpcUrl.trim() || !/^https?:\/\//i.test(rpcUrl.trim())) return

    let cancelled = false
    const timer = setTimeout(async () => {
      setRpcStatus('validating')
      const result = await networkValidationService.validateRpc(rpcUrl.trim())
      if (cancelled) return

      if (!result.isValid || !result.detectedChainId) {
        setRpcStatus('invalid')
        return
      }

      const exists = getAllNetworks().some(
        (n) => n.id !== network.id && n.chainId === result.detectedChainId
      )
      if (exists) {
        setRpcStatus('exists')
        return
      }

      setDetectedChainId(result.detectedChainId)
      setRpcStatus('valid')
    }, 600)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpcUrl, isEditing, network?.id, network?.rpcUrl, network?.chainId])

  const handlePasteTrim =
    (setter: (v: string) => void) =>
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault()
      setter(e.clipboardData.getData('text').trim())
    }

  const handleEnterEdit = () => {
    if (!network) return
    setNetworkName(network.name)
    setRpcName(network.rpcName ?? '')
    setRpcUrl(network.rpcUrl)
    setDetectedChainId(network.chainId)
    setCurrencySymbol(network.nativeCurrency?.symbol ?? '')
    setBlockExplorerUrl(network.blockExplorerUrl ?? '')
    setRpcStatus('valid')
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setRpcStatus('idle')
  }

  const handleSaveClick = () => {
    if (!network || rpcStatus !== 'valid' || !detectedChainId || isSubmitting)
      return

    if (detectedChainId !== network.chainId) {
      setShowChainIdConfirm(true)
      return
    }

    performSave()
  }

  const performSave = () => {
    if (!network || !detectedChainId) return

    const chainIdChanged = detectedChainId !== network.chainId

    setIsSubmitting(true)

    const result = updateCustomNetwork(network.id, {
      name: networkName.trim(),
      rpcName: rpcName.trim() || undefined,
      rpcUrl: rpcUrl.trim(),
      chainId: detectedChainId,
      blockExplorerUrl: blockExplorerUrl.trim() || undefined,
      nativeCurrency: {
        name: currencySymbol.trim(),
        symbol: currencySymbol.trim(),
        decimals: network.nativeCurrency?.decimals ?? 18
      }
    })

    setIsSubmitting(false)

    if (result.success) {
      providerService.clearProvider(network.id)
      if (chainIdChanged && network.id === selectedNetwork) {
        resetForWalletSwitch()
      }
      setIsEditing(false)
      setRpcStatus('idle')
      setSnackbarState({
        isOpen: true,
        message: t('networks.update_success'),
        type: 'success'
      })
    } else {
      setSnackbarState({
        isOpen: true,
        message: t('add_network.network_already_exists'),
        type: 'error'
      })
    }
  }

  const handleRemoveClick = () => {
    if (!network) return
    setShowRemoveConfirm(true)
  }

  const performRemove = () => {
    if (!network) return

    removeCustomNetwork(network.id)
    if (selectedNetwork === network.id) {
      setSelectedNetwork('omdb')
    }
    navigate('/networks')
  }

  if (!network) {
    return (
      <div className="flex flex-col w-full max-w-lg mx-auto gap-8 pt-4">
        <div className="hidden lg:flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-text-main/5 hover:bg-text-main/10 transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-text-main" />
          </button>
        </div>
        <p className="text-text-secondary text-center">
          {t('networks.no_results')}
        </p>
      </div>
    )
  }

  const fields = [
    { label: t('add_network.network_name'), value: network.name },
    { label: t('add_network.rpc_name'), value: network.rpcName },
    { label: t('add_network.rpc_url'), value: network.rpcUrl },
    { label: t('add_network.chain_id'), value: String(network.chainId) },
    {
      label: t('add_network.currency_symbol'),
      value: network.nativeCurrency?.symbol
    },
    {
      label: t('add_network.block_explorer_url'),
      value: network.blockExplorerUrl
    }
  ].filter((field) => !!field.value)

  const showChainIdWarning =
    rpcStatus === 'valid' &&
    detectedChainId !== null &&
    detectedChainId !== network.chainId

  return (
    <div className="flex flex-col w-full max-w-lg mx-auto gap-8 pt-4">
      <div className="hidden lg:flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full bg-text-main/5 hover:bg-text-main/10 transition-colors"
        >
          <ArrowLeft className="w-6 h-6 text-text-main" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full overflow-hidden bg-primary/20 flex items-center justify-center shrink-0">
            {network.logoUrl ? (
              <img
                src={network.logoUrl}
                alt={network.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <Globe className="w-4 h-4 text-primary" />
            )}
          </div>
          <h1 className="text-3xl font-bold text-text-main">
            {network.name}
          </h1>
        </div>
      </div>

      {!isEditing && (
        <div className="bg-card border border-border-base rounded-3xl p-8 flex flex-col gap-6 shadow-xl">
          {fields.map((field) => (
            <div key={field.label} className="flex flex-col gap-2">
              <label className="text-xs text-text-secondary uppercase tracking-widest font-bold ml-1">
                {field.label}
              </label>
              <input
                type="text"
                value={field.value}
                disabled
                readOnly
                className="w-full p-4 rounded-2xl bg-text-main/5 border border-transparent outline-none text-text-main disabled:opacity-60"
              />
            </div>
          ))}
        </div>
      )}

      {isEditing && (
        <form
          id="edit-network-form"
          onSubmit={(e) => {
            e.preventDefault()
            handleSaveClick()
          }}
          className="bg-card border border-border-base rounded-3xl p-8 flex flex-col gap-6 shadow-xl"
        >
          <div className="flex flex-col gap-2">
            <label className="text-xs text-text-secondary uppercase tracking-widest font-bold ml-1">
              {t('add_network.network_name')}
            </label>
            <input
              type="text"
              value={networkName}
              onChange={(e) => setNetworkName(e.target.value)}
              onPaste={handlePasteTrim(setNetworkName)}
              className="w-full p-4 rounded-2xl bg-text-main/5 border border-transparent focus:border-primary/50 outline-none transition-all text-text-main"
              required
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs text-text-secondary uppercase tracking-widest font-bold ml-1">
              {t('add_network.rpc_name')}
            </label>
            <input
              type="text"
              value={rpcName}
              onChange={(e) => setRpcName(e.target.value)}
              onPaste={handlePasteTrim(setRpcName)}
              className="w-full p-4 rounded-2xl bg-text-main/5 border border-transparent focus:border-primary/50 outline-none transition-all text-text-main"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs text-text-secondary uppercase tracking-widest font-bold ml-1">
              {t('add_network.rpc_url')}
            </label>
            <input
              type="text"
              value={rpcUrl}
              onChange={(e) => setRpcUrl(e.target.value)}
              onPaste={handlePasteTrim(setRpcUrl)}
              placeholder="https://..."
              className="w-full p-4 rounded-2xl bg-text-main/5 border border-transparent focus:border-primary/50 outline-none transition-all text-text-main font-mono"
              required
            />
            {rpcStatus === 'validating' && (
              <p className="flex items-center gap-1.5 text-xs text-text-secondary">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {t('add_network.validating')}
              </p>
            )}
            {rpcStatus === 'valid' && !showChainIdWarning && (
              <p className="flex items-center gap-1.5 text-xs text-success">
                <Check className="w-3.5 h-3.5" />
                {t('add_network.rpc_valid')}
              </p>
            )}
            {rpcStatus === 'valid' && showChainIdWarning && (
              <p className="flex items-center gap-1.5 text-xs text-yellow-500">
                <AlertCircle className="w-3.5 h-3.5" />
                {t('networks.chain_id_changed_warning', {
                  oldChainId: network.chainId,
                  newChainId: detectedChainId
                })}
              </p>
            )}
            {rpcStatus === 'invalid' && (
              <p className="flex items-center gap-1.5 text-xs text-red-500">
                <AlertCircle className="w-3.5 h-3.5" />
                {t('add_network.rpc_invalid')}
              </p>
            )}
            {rpcStatus === 'exists' && (
              <p className="flex items-center gap-1.5 text-xs text-red-500">
                <AlertCircle className="w-3.5 h-3.5" />
                {t('add_network.network_already_exists')}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs text-text-secondary uppercase tracking-widest font-bold ml-1">
              {t('add_network.chain_id')}
            </label>
            <input
              type="text"
              value={detectedChainId ?? ''}
              placeholder={t('add_network.chain_id_placeholder')}
              disabled
              readOnly
              className="w-full p-4 rounded-2xl bg-text-main/5 border border-transparent outline-none transition-all text-text-main disabled:opacity-60"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs text-text-secondary uppercase tracking-widest font-bold ml-1">
              {t('add_network.currency_symbol')}
            </label>
            <input
              type="text"
              value={currencySymbol}
              onChange={(e) => setCurrencySymbol(e.target.value)}
              onPaste={handlePasteTrim(setCurrencySymbol)}
              className="w-full p-4 rounded-2xl bg-text-main/5 border border-transparent focus:border-primary/50 outline-none transition-all text-text-main"
              required
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs text-text-secondary uppercase tracking-widest font-bold ml-1">
              {t('add_network.block_explorer_url')}{' '}
              <span className="normal-case font-normal text-text-secondary">
                {t('add_network.optional_label')}
              </span>
            </label>
            <input
              type="text"
              value={blockExplorerUrl}
              onChange={(e) => setBlockExplorerUrl(e.target.value)}
              onPaste={handlePasteTrim(setBlockExplorerUrl)}
              placeholder="https://..."
              className="w-full p-4 rounded-2xl bg-text-main/5 border border-transparent focus:border-primary/50 outline-none transition-all text-text-main font-mono"
            />
          </div>
        </form>
      )}

      {network.isCustom && !isEditing && (
        <div className="flex gap-3">
          <button
            onClick={handleEnterEdit}
            className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-text-main border border-border-base hover:bg-text-main/5 transition-colors"
          >
            <Pencil className="w-4 h-4" />
            {t('networks.edit_network')}
          </button>
          <button
            onClick={handleRemoveClick}
            className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-red-500 border border-red-500/30 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            {t('networks.remove_network')}
          </button>
        </div>
      )}

      {network.isCustom && isEditing && (
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleCancelEdit}
            className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-text-main border border-border-base hover:bg-text-main/5 transition-colors"
          >
            <X className="w-4 h-4" />
            {t('dialogs.cancel')}
          </button>
          <button
            type="submit"
            form="edit-network-form"
            disabled={rpcStatus !== 'valid' || isSubmitting}
            className="flex-1 btn-gradient-fill py-4 rounded-2xl font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('networks.save_changes')}
          </button>
        </div>
      )}

      <Snackbar
        message={snackbarState.message}
        isOpen={snackbarState.isOpen}
        type={snackbarState.type}
        onClose={() => setSnackbarState((prev) => ({ ...prev, isOpen: false }))}
      />

      <ConfirmDialog
        open={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        onConfirm={performRemove}
        variant="danger"
        title={t('networks.confirm_remove_title')}
        description={t('networks.confirm_remove')}
        confirmLabel={t('networks.remove_network')}
      />

      <ConfirmDialog
        open={showChainIdConfirm}
        onClose={() => setShowChainIdConfirm(false)}
        onConfirm={performSave}
        variant="warning"
        title={t('networks.confirm_chain_id_change_title')}
        description={t('networks.confirm_chain_id_change', {
          oldChainId: network.chainId,
          newChainId: detectedChainId
        })}
        confirmLabel={t('networks.save_changes')}
      />
    </div>
  )
}

export default NetworkDetails
